import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AlumniLayout from "@/components/AlumniLayout";
import { toast } from "sonner";
import { CalendarHeart, MapPin, ExternalLink, ArrowLeft, Check, HelpCircle, X, Upload, Trash2, Image as ImageIcon, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "Date TBD";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const OPTIONS = [
  { value: "attending", label: "Attending", icon: Check, cls: "border-emerald-500 bg-emerald-50 text-emerald-800" },
  { value: "maybe", label: "Maybe", icon: HelpCircle, cls: "border-amber-500 bg-amber-50 text-amber-800" },
  { value: "declined", label: "Can't make it", icon: X, cls: "border-red-400 bg-red-50 text-red-800" },
];

export default function AlumniEventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [e, setE] = useState(null);
  const [status, setStatus] = useState("");
  const [guests, setGuests] = useState(0);
  const [savingRsvp, setSavingRsvp] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);

  const load = () => {
    api.get(`/experience/events/${id}`).then((r) => {
      setE(r.data);
      const cur = r.data.my_rsvp;
      if (cur && cur.status !== "invited") { setStatus(cur.status); setGuests(cur.guests || 0); }
    }).catch((err) => toast.error(apiError(err.response?.data?.detail)));
  };
  useEffect(load, [id]);

  useEffect(() => {
    if (lightbox === null) return;
    const total = (e?.photos || []).length;
    const onKey = (ev) => {
      if (ev.key === "Escape") setLightbox(null);
      else if (ev.key === "ArrowRight") setLightbox((i) => (i + 1) % total);
      else if (ev.key === "ArrowLeft") setLightbox((i) => (i - 1 + total) % total);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, e]);

  const saveRsvp = async () => {
    if (!status) { toast.error("Choose a response first"); return; }
    setSavingRsvp(true);
    try {
      await api.post(`/experience/events/${id}/rsvp`, { status, guests: status === "attending" ? guests : 0 });
      toast.success("Your RSVP has been saved");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingRsvp(false); }
  };

  const onPick = async (ev) => {
    const files = Array.from(ev.target.files || []);
    if (!files.length) return;
    const images = files.filter((f) => f.type.startsWith("image/"));
    if (!images.length) { toast.error("Please choose image files"); return; }
    setUploading(true);
    let ok = 0, failed = 0, dupes = 0;
    const seenHashes = new Set();
    for (const file of images) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        // Skip if the same image was already chosen in this selection.
        if (data.hash && seenHashes.has(data.hash)) { dupes += 1; continue; }
        if (data.hash) seenHashes.add(data.hash);
        const { data: added } = await api.post(`/experience/events/${id}/photos`, { path: data.path, thumb: data.thumb || "", caption: "", hash: data.hash });
        if (added && added.duplicate) dupes += 1; else ok += 1;
      } catch (err) { failed += 1; }
    }
    if (ok) toast.success(`Added ${ok} photo${ok === 1 ? "" : "s"} to the gallery`);
    if (dupes) toast.info(`Skipped ${dupes} duplicate photo${dupes === 1 ? "" : "s"} already in the gallery`);
    if (failed) toast.error(`${failed} photo${failed === 1 ? "" : "s"} failed to upload`);
    load();
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = async (pid) => {
    if (!window.confirm("Remove this photo?")) return;
    try { await api.delete(`/experience/events/${id}/photos/${pid}`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (!e) return <AlumniLayout wide><div className="text-muted-foreground">Loading…</div></AlumniLayout>;
  const loc = e.location || {};
  const photos = e.photos || [];
  const isPast = e.status === "completed" || (() => { const t = e.end || e.start; return t && new Date(t).getTime() < Date.now(); })();

  return (
    <AlumniLayout wide>
      <button data-testid="aevent-back" onClick={() => navigate("/experience/events")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-4"><ArrowLeft className="h-4 w-4" /> Back to Events</button>

      <div className="bg-card border border-border rounded-lg shadow-sm p-6">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-accent/20 text-primary">{e.category}</span>
        <h1 className="font-serif text-3xl tracking-tight flex items-start gap-2 mt-2"><CalendarHeart className="h-7 w-7 text-primary shrink-0 mt-1" />{e.title}</h1>
        <div className="text-sm text-muted-foreground mt-2">{fmtDate(e.start)}{e.end ? ` — ${fmtDate(e.end)}` : ""}</div>
        {(loc.venue || loc.city) && <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1"><MapPin className="h-4 w-4" />{[loc.venue, loc.address, loc.city, loc.state].filter(Boolean).join(", ")}</div>}
        {loc.virtual_url && <a href={loc.virtual_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm text-primary mt-1 hover:underline"><ExternalLink className="h-3.5 w-3.5" />{loc.virtual_url}</a>}
        {e.description && <p className="text-sm mt-3 max-w-2xl leading-relaxed">{e.description}</p>}
        {e.ticket_price ? <p className="text-sm mt-2">Ticket: <b>${e.ticket_price.toFixed(2)}</b></p> : <p className="text-sm mt-2 text-muted-foreground">Free event</p>}
      </div>

      {!isPast && (
      <div className="mt-6 bg-card border border-border rounded-lg shadow-sm p-6">
        <h2 className="font-serif text-xl tracking-tight mb-1">Will you join us?</h2>
        <p className="text-sm text-muted-foreground mb-4">{e.my_rsvp && e.my_rsvp.status !== "invited" ? `Your current response: ${e.my_rsvp.status}. You can change it anytime.` : "Let your class know you're coming."}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {OPTIONS.map((o) => {
            const Icon = o.icon; const active = status === o.value;
            return (
              <button key={o.value} data-testid={`aevent-rsvp-${o.value}`} onClick={() => setStatus(o.value)} className={`flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 text-sm font-medium transition-all ${active ? o.cls : "border-border text-muted-foreground hover:border-primary/50"}`}>
                <Icon className="h-4 w-4" /> {o.label}
              </button>
            );
          })}
        </div>
        {status === "attending" && (
          <label className="block mt-4">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Guests you're bringing</span>
            <input data-testid="aevent-guests" type="number" min="0" value={guests} onChange={(ev) => setGuests(parseInt(ev.target.value || "0", 10))} className="ud-input mt-1 max-w-[120px]" />
          </label>
        )}
        <button data-testid="aevent-rsvp-save" onClick={saveRsvp} disabled={savingRsvp} className="mt-4 px-6 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{savingRsvp ? "Saving…" : "Save RSVP"}</button>
      </div>
      )}

      <div className="mt-6 bg-card border border-border rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-serif text-xl tracking-tight flex items-center gap-2"><ImageIcon className="h-5 w-5 text-primary" /> Photo Gallery <span className="text-sm text-muted-foreground">({photos.length})</span></h2>
          <input ref={fileRef} data-testid="aevent-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={onPick} />
          <button data-testid="aevent-photo-upload" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-4 py-2 rounded-md bg-secondary text-white text-sm font-medium hover:bg-secondary/90 disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {uploading ? "Uploading…" : "Add Photo"}
          </button>
        </div>
        {photos.length === 0 && <div className="text-sm text-muted-foreground text-center py-8" data-testid="aevent-gallery-empty">No photos yet. Be the first to share a memory from this event!</div>}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p) => (
            <div key={p.id} data-testid={`aevent-photo-${p.id}`} className="relative group rounded-lg overflow-hidden border border-border aspect-square bg-muted">
              <img src={fileUrl(p.thumb || p.path)} alt={p.caption || "Event photo"} loading="lazy" decoding="async" onClick={() => openLightbox(photos.indexOf(p))} className="h-full w-full object-cover cursor-pointer transition-transform group-hover:scale-105" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                <div className="text-[10px] text-white/90 truncate">{p.uploaded_by_name || "Alumnus"}</div>
              </div>
              {(user?.role === "admin" || user?.role === "superadmin" || p.uploaded_by === user?.alumnus_id) && (
                <button data-testid={`aevent-photo-del-${p.id}`} onClick={(ev) => { ev.stopPropagation(); removePhoto(p.id); }} title="Delete photo" className="absolute top-1.5 right-1.5 p-1.5 rounded-md bg-black/55 text-white hover:bg-red-600 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
          ))}
        </div>
      </div>

      {lightbox !== null && photos[lightbox] && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={closeLightbox} data-testid="aevent-lightbox">
          <button data-testid="lightbox-close" onClick={closeLightbox} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"><X className="h-5 w-5" /></button>
          <div className="absolute top-5 left-1/2 -translate-x-1/2 text-xs font-mono text-white/70 tracking-wider">{lightbox + 1} / {photos.length}</div>

          {photos.length > 1 && (
            <button data-testid="lightbox-prev" onClick={(ev) => { ev.stopPropagation(); setLightbox((i) => (i - 1 + photos.length) % photos.length); }}
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors z-10"><ChevronLeft className="h-6 w-6" /></button>
          )}

          <figure className="max-w-[92vw] max-h-[88vh] flex flex-col items-center" onClick={(ev) => ev.stopPropagation()}>
            <img src={fileUrl(photos[lightbox].path)} alt={photos[lightbox].caption || "Event photo"} className="max-h-[80vh] max-w-full rounded-lg shadow-2xl object-contain" />
            <figcaption className="mt-3 text-center text-sm text-white/80">
              {photos[lightbox].caption && <span className="block">{photos[lightbox].caption}</span>}
              <span className="text-white/50 text-xs">Shared by {photos[lightbox].uploaded_by_name || "Alumnus"}</span>
            </figcaption>
          </figure>

          {photos.length > 1 && (
            <button data-testid="lightbox-next" onClick={(ev) => { ev.stopPropagation(); setLightbox((i) => (i + 1) % photos.length); }}
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors z-10"><ChevronRight className="h-6 w-6" /></button>
          )}
        </div>,
        document.body
      )}
    </AlumniLayout>
  );
}
